<template>
  <div>
    <label>
      <b> {{ main_text }} </b>
    </label>
    <!-- sort in draggable to prevent reordering when editing-->
    <draggable
      v-model="local_value"
      class="drag-area synaplus-cosmo-scroll-higher-720"
      group="col"
      handle=".col-handle"
      :sort="editing_nb == -1"
    >
      <div v-for="(val, index) in local_value" :key="JSON.stringify(val)">
        <slot
          name="sub_component"
          v-bind="{
            ...get_sub_component_props(false, val, index),
            class: 'mt-1',
          }"
        />
      </div>
    </draggable>
    <slot
      name="sub_component"
      v-bind="{
        ...get_sub_component_props(true, new_column_entry),
        class: 'mt-4',
      }"
    />
  </div>
</template>

<script>
import _ from 'lodash'

import Vue from 'vue'
import draggable from 'vuedraggable'

import { GenericColumnName } from '@/components/object_model.js'
import GenericColumnNameEdition from '@/components/generic_column_name_edition.vue'

// *****************************************************************************

export default {
  name: 'ColumnsChoiceOptions',
  components: {
    draggable,
    GenericColumnNameEdition,
  },
  props: [
    'value',
    'main_text',
    'available_column_names',
    'can_reuse_column_names',
    'entry_constructor',
  ],
  data() {
    return {
      local_value: undefined,
      editing_nb: -1,
      new_column_entry: new (this.normalized_entry_constructor())(),
    }
  },

  computed: {
    available_unused_column_names() {
      let res
      if (this.can_reuse_column_names) {
        res = _.cloneDeep(this.available_column_names)
      } else {
        let selected_column_names = _.map(
          this.local_value,
          elt => elt.get_generic_column_name().name
        )
        res = _.filter(
          this.available_column_names,
          x => !selected_column_names.includes(x)
        )
      }

      return res
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, ov)) {
          this.local_value = _.cloneDeep(nv)
        }
      },
    },

    local_value: {
      immediate: true,
      handler(v) {
        if (!_.isEqual(v, this.value)) {
          this.$emit('input', _.cloneDeep(v))
        }
      },
    },
  },

  methods: {
    add_new_column_name(entry) {
      this.new_column_entry = entry
      this.local_value.push(entry)
      this.remove_potential_duplicate(this.local_value.length - 1, entry)
      Vue.nextTick(
        () =>
          (this.new_column_entry = new (this.normalized_entry_constructor())())
      )
    },

    validate_column_name_edition(index, entry) {
      Vue.set(this.local_value, index, entry)
      this.remove_potential_duplicate(index, entry)
    },

    remove_potential_duplicate(index, entry) {
      let duplicate_index = _.findIndex(this.local_value, x =>
        _.isMatchWith(x, entry, _.isEqual())
      )
      if (duplicate_index !== index) {
        if (duplicate_index > index) {
          duplicate_index += 1
        }
        this.local_value.splice(duplicate_index, 1)
      }
      this.editing_nb = -1
    },

    trash_column_name(index) {
      this.editing_nb = -1
      this.local_value.splice(index, 1)
    },

    select_all() {
      if (this.available_unused_column_names) {
        this.local_value = this.local_value.concat(
          _.map(
            this.available_unused_column_names,
            column_name =>
              new (this.normalized_entry_constructor())(column_name, false)
          )
        )
      }
    },
    deselect_all() {
      this.local_value = []
    },

    get_sub_component_props(is_new_entry, entry, index) {
      let available_unused_column_names = this.available_unused_column_names
      let available_column_names = this.available_column_names
      let is_new_column_name = is_new_entry
      let in_edition = is_new_entry ? true : this.editing_nb == index

      let listeners = is_new_entry
        ? {
            input: this.add_new_column_name.bind(this),
            select_all: this.select_all.bind(this),
            deselect_all: this.deselect_all.bind(this),
          }
        : {
            start_editing: () => {
              this.editing_nb = index
            },
            trash: () => this.trash_column_name(index),
            input: val => {
              this.validate_column_name_edition(index, val)
            },
          }

      let res = {
        value: entry,
        available_unused_column_names,
        available_column_names,
        is_new_column_name,
        in_edition,
        listeners,
      }

      return res
    },
    normalized_entry_constructor() {
      // Is in methods because if in computed, it's not initialized in data
      let res = this.entry_constructor
        ? this.entry_constructor
        : GenericColumnName
      return res
    },
  },
}
</script>
